import { useAtom } from 'jotai';
import { Toast } from 'primereact/toast';
import { useEffect, useMemo, useState } from 'react';
import { endpointFundDocumentUrlGET } from '~/api/def/endpoints/document';
import { toastAtom } from '~/atoms/toastAtom';
import { useDocumentOverviewApi } from '~/components/ui/document/dialogs/documentOverviewDialog/useDocumentOverviewApi';
import { FormFieldCorrection } from '~/components/ui/vendor/pdf/document_pdf_viewer/FieldCorrectionForm';
import { PDFContainer } from '~/components/ui/vendor/pdf/pdf_container';
import { type BoundingBox, setDocumentField } from '~/lib/document_field';
import { clausesToPDFHighlights } from '~/lib/pdfViewer/clausesToPDFHighlights';
import { fieldsToPDFHighlights } from '~/lib/pdfViewer/fieldsToPDFHighlights';
import { ExpandableTip } from './ExpandableTip';
import type { DocumentPDFViewerProps } from './types';

export function DocumentPDFViewer(props: DocumentPDFViewerProps) {
  const { documentId, overrideActiveHighlight, fields, clauses } = props;
  const [isSubmittingUpdate, setIsSubmittingUpdate] = useState(false);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [toastCurrent] = useAtom<Toast | null>(toastAtom);
  const { fetchDocumentFields } = useDocumentOverviewApi({ documentId });
  useEffect(() => {
    setDocumentUrl(null);
    endpointFundDocumentUrlGET(documentId).then((res) => {
      if (res?.success) {
        setDocumentUrl(res.data.presignedUrl);
      }
    });
  }, [documentId]);
  ``;

  const highlights = useMemo(() => {
    const fieldHighlights = fields ? fieldsToPDFHighlights(fields) : [];
    const clauseHighlights = clauses ? clausesToPDFHighlights(clauses) : [];
    return [...fieldHighlights, ...clauseHighlights];
  }, [fields, clauses]);

  const addHighlight = useMemo(() => {
    return (highlight: BoundingBox, data: FormFieldCorrection) => {
      if (isSubmittingUpdate) {
        return null;
      }
      setIsSubmittingUpdate(true);
      setDocumentField({
        highlight,
        documentId: documentId,
        fieldId: data.fieldId,
        fieldValue: data.fieldValue,
      })
        .then((res) => {
          setIsSubmittingUpdate(false);
          if (res && res.response === 200) {
            toastCurrent?.show({
              severity: 'success',
              summary: 'Form Submitted',
              detail: 'Field value updated successfully.',
            });
            fetchDocumentFields();
          } else {
            toastCurrent?.show({
              severity: 'error',
              summary: 'Form Submitted',
              detail: 'Something went wrong while updating field value',
            });
          }
        })
        .catch(() => {
          toastCurrent?.show({
            severity: 'error',
            summary: 'Form Submitted',
            detail: 'Something went wrong while updating field value',
          });
        });
    };
  }, [isSubmittingUpdate, documentId, toastCurrent, fetchDocumentFields]);

  if (!documentUrl) {
    return <></>;
  } else {
    return (
      <PDFContainer
        activeHighlight={props.activeHighlight}
        url={documentUrl}
        highlights={highlights}
        onScrollAway={props.onScrollAway}
        onLoad={props.onLoad}
        enableAreaSelection={(event) => event.altKey}
        overrideActiveHighlight={overrideActiveHighlight}
        selectionTip={
          addHighlight &&
          props.fieldTypes && (
            <ExpandableTip
              fieldTypes={props.fieldTypes}
              addHighlight={addHighlight}
            />
          )
        }
      />
    );
  }
}
